import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, SearchX, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import StockSearch from "@/components/StockSearch";
import { stashTickerProvenance } from "@/lib/ticker_provenance";
import { startAnalyze } from "@/lib/startAnalyze";
import { emitLifecycle } from "@/lib/lifecycle";
import { useTranslation } from "@/lib/i18n";

/**
 * StockNotFound — premium dark error state shown on the Analyze page when:
 *   • The backend returns 404 for the requested ticker
 *   • The FMP / mock provider has no record of the symbol
 *   • The analysis request times out
 *   • An unexpected backend error occurs
 *   • The ticker is a non-equity asset (crypto / FX / futures) — Stockaily
 *     is an equity-only platform; this surfaces a dedicated message.
 *
 * Mirrors Stockaily's existing dark aesthetic so it never feels like a
 * generic browser 404 page. The popular-ticker chips give the user a
 * one-tap way to recover from a typo without losing momentum.
 */
const SUGGESTED_TICKERS = [
  { ticker: "AAPL", label: "Apple" },
  { ticker: "MSFT", label: "Microsoft" },
  { ticker: "NVDA", label: "NVIDIA" },
  { ticker: "GOOGL", label: "Alphabet" },
];

export default function StockNotFound({
  ticker,
  reason = "not_found",
  testId = "stock-not-found",
  requestId = null,
  isAdmin = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isUnsupported = reason === "unsupported_asset";
  const tickerUpper = (ticker || "").toUpperCase();

  // Phase 6.AM — Emit `error_screen_rendered` the moment THIS component
  // mounts. Independent of TimeoutScreenTelemetry, so the Owner Console
  // gets a row even when StockNotFound is rendered outside Analyze.js
  // (e.g. as a fallback from a different page). Carries the exact
  // `reason` that drove the headline so the operator never has to guess
  // whether the user saw "Something went wrong" vs "Analysis took too
  // long" vs "We couldn't find this stock".
  React.useEffect(() => {
    try {
      emitLifecycle("error_screen_rendered", {
        ticker: ticker || null,
        request_id: requestId || null,
        extra: {
          source: "stock_not_found",
          reason,
          headline:
            isUnsupported ? "Unsupported asset"
            : reason === "timeout" ? "Analysis took too long"
            : reason === "error" ? "Something went wrong"
            : "We couldn’t find this stock",
          is_admin: !!isAdmin,
          pathname: (typeof window !== "undefined") ? window.location.pathname : null,
        },
      });
    } catch { /* swallow */ }
    // Single-fire on mount. The reason / ticker should be stable for
    // the lifetime of this component (it's a leaf in the render tree).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headline = isUnsupported
    ? t("analyze.not_found.head_unsupported")
    : reason === "timeout"
      ? t("analyze.not_found.head_timeout")
      : reason === "auth_expired"
        ? t("analyze.not_found.head_session_expired")
        : reason === "error"
          ? t("analyze.not_found.head_error")
          : t("analyze.not_found.head_default");

  const body = isUnsupported
    ? t("analyze.not_found.body_unsupported", { ticker: tickerUpper })
    : reason === "timeout"
      ? t("analyze.not_found.body_timeout")
      : reason === "auth_expired"
        ? t("analyze.not_found.body_session_expired", { ticker: tickerUpper || t("analyze.not_found.this_stock") })
        : reason === "error"
          ? t("analyze.not_found.body_error")
          : t("analyze.not_found.body_default", { ticker: tickerUpper || t("analyze.not_found.this_stock") });

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid={testId}
      className="mx-auto mt-8 sm:mt-12 max-w-2xl"
    >
      <div className="rounded-2xl border border-border/70 bg-card/60 p-6 sm:p-10 shadow-[var(--app-shadow-elev-1)] backdrop-blur">
        {/* Icon + headline */}
        <div className="flex flex-col items-center text-center">
          <div
            className="inline-flex w-14 h-14 sm:w-16 sm:h-16 items-center justify-center rounded-2xl mb-4"
            style={{
              background: "rgba(229,72,77,0.10)",
              border: "1px solid rgba(229,72,77,0.30)",
              color: "#E5484D",
            }}
            aria-hidden
          >
            <SearchX className="w-7 h-7 sm:w-8 sm:h-8" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight">
            {headline}
          </h1>
          <p className="mt-2 text-sm sm:text-[15px] text-muted-foreground leading-relaxed max-w-md">
            {body}
          </p>
          {/* Phase 6.AE — visible request_id for admins so the operator
              can paste it into Owner Console search / grep Mongo to find
              the exact wire-level audit row for this failure. */}
          {isAdmin && requestId ? (
            <div
              className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-700"
              data-testid={`${testId}-request-id`}
            >
              <span className="opacity-70">request_id</span>
              <code className="font-mono">{requestId}</code>
            </div>
          ) : null}
        </div>

        {/* Phase 6.AR — Sign-in CTA shown ONLY for auth_expired reason.
            Replaces the search box for this case since the user must
            re-authenticate before any analyze can succeed. */}
        {reason === "auth_expired" ? (
          <div className="mt-6 sm:mt-7 flex flex-col items-center gap-3">
            <Button
              onClick={() => navigate("/login")}
              data-testid={`${testId}-signin-cta`}
              className="px-6"
            >
              {t("analyze.not_found.sign_in_continue")}
            </Button>
            <p className="text-[11px] text-muted-foreground">
              {t("analyze.not_found.back_after_signin", { ticker: tickerUpper || t("analyze.not_found.back_after_signin_default") })}
            </p>
          </div>
        ) : (
          <>
        {/* Search input */}
        <div className="mt-6 sm:mt-7">
          <StockSearch
            testIdPrefix={`${testId}-search`}
            placeholder={isUnsupported ? t("analyze.not_found.search_placeholder_unsupported") : t("analyze.not_found.search_placeholder_default")}
            autoFocus={false}
          />
        </div>

        {/* Hint line + suggestions */}
        <div className="mt-4 text-[11.5px] text-muted-foreground/80 text-center">
          {isUnsupported ? (
            <span dangerouslySetInnerHTML={{ __html: t("analyze.not_found.tip_unsupported_html") }} />
          ) : (
            <span dangerouslySetInnerHTML={{ __html: t("analyze.not_found.tip_default_html") }} />
          )}
        </div>

        <div className="mt-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-[#3182CE]" />
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {isUnsupported ? t("analyze.not_found.suggestions_unsupported") : t("analyze.not_found.suggestions_default")}
            </span>
          </div>
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-2"
            data-testid={`${testId}-suggestions`}
          >
            {SUGGESTED_TICKERS.map((s) => (
              <button
                key={s.ticker}
                type="button"
                onClick={() => {
                  startAnalyze({ navigate, ticker: s.ticker, source: "stock_not_found:suggestion" });
                }}
                data-testid={`${testId}-suggestion-${s.ticker}`}
                className="group flex flex-col items-start gap-0.5 rounded-xl border border-border/70 bg-card/40 px-3 py-3 text-left transition-colors hover:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-glow-blue)]"
              >
                <span className="font-mono text-sm tracking-wider text-foreground/95">
                  {s.ticker}
                </span>
                <span className="text-[11.5px] text-muted-foreground group-hover:text-foreground/90 transition-colors">
                  {s.label}
                </span>
              </button>
            ))}
          </div>
        </div>
        </>
        )}

        {/* Back button */}
        <div className="mt-6 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            data-testid={`${testId}-back`}
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> {t("analyze.not_found.go_back")}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
